import { useEffect, useRef } from "react";

import type { ArmySupplyView, LaneViewId, SupplyViewModel } from "../presentation/game-view-model";
import { formatResources } from "../presentation/resource-format";

interface SupplyChoicePanelProps {
  supply: SupplyViewModel;
  selectedLaneId: LaneViewId | undefined;
  selectedArmyInstanceId: string | undefined;
  validationMessage: string | undefined;
  onSelectArmy: (armyCardInstanceId: string) => void;
  onConfirmDiscard: () => void;
  onCancel: () => void;
}

function armyLabel(army: ArmySupplyView) {
  return `${army.name}. Upkeep ${formatResources(army.upkeep)}. Select to remove for Upkeep.`;
}

export function SupplyChoicePanel({
  supply,
  selectedLaneId,
  selectedArmyInstanceId,
  validationMessage,
  onSelectArmy,
  onConfirmDiscard,
  onCancel,
}: SupplyChoicePanelProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const lane = supply.lanes.find(
    (candidate) =>
      candidate.laneId === selectedLaneId && supply.unsustainableLaneIds.includes(candidate.laneId),
  );
  const selectedArmy = lane?.armies.find(
    (army) => army.instanceId === selectedArmyInstanceId && army.legalDiscard,
  );
  const focusedLaneId = lane?.laneId;

  useEffect(() => {
    if (focusedLaneId !== undefined) titleRef.current?.focus({ preventScroll: true });
  }, [focusedLaneId]);

  return (
    <section
      className="supply-choice"
      aria-labelledby="supply-choice-title"
      data-testid="supply-choice-panel"
    >
      <header className="supply-choice__header">
        <div>
          <span className="eyebrow">Mandatory local Upkeep</span>
          <h2 id="supply-choice-title" ref={titleRef} tabIndex={-1}>
            {lane === undefined ? "Supply shortfall" : `${lane.label} lane - Supply shortfall`}
          </h2>
        </div>
        <span className="supply-choice__warning" role="status" aria-live="polite">
          {supply.unsustainableLaneIds.length} lane
          {supply.unsustainableLaneIds.length === 1 ? "" : "s"} unresolved
        </span>
      </header>

      {lane === undefined ? (
        <p className="supply-choice__prompt">
          Choose a highlighted lane. Supply cannot advance until every local shortfall is resolved.
        </p>
      ) : (
        <>
          <dl className="supply-resources" aria-label={`${lane.label} lane Supply calculation`}>
            <div>
              <dt>Produces</dt>
              <dd>{formatResources(lane.production)}</dd>
            </div>
            <div>
              <dt>Requires</dt>
              <dd>{formatResources(lane.upkeep)}</dd>
            </div>
            <div className="supply-resources__missing">
              <dt>Missing</dt>
              <dd>{formatResources(lane.shortfall)}</dd>
            </div>
          </dl>

          <div className="supply-armies" aria-label="Armies available to discard">
            {lane.armies.map((army) => (
              <button
                key={army.instanceId}
                className="supply-army"
                type="button"
                aria-label={armyLabel(army)}
                aria-pressed={army.instanceId === selectedArmyInstanceId}
                data-testid="supply-army-choice"
                disabled={!army.legalDiscard}
                onClick={() => onSelectArmy(army.instanceId)}
              >
                <strong>{army.name}</strong>
                <span>Upkeep {formatResources(army.upkeep)}</span>
              </button>
            ))}
          </div>

          <p className="supply-choice__preview" aria-live="polite">
            {selectedArmy === undefined
              ? "Choose an Army to preview its required Upkeep removal."
              : `${selectedArmy.name} must enter your discard pile to resolve this shortfall.`}
          </p>
        </>
      )}

      {validationMessage ? (
        <p className="validation-message" role="status">
          {validationMessage}
        </p>
      ) : null}

      <div className="supply-choice__actions">
        <button className="action-button" type="button" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="action-button action-button--primary"
          type="button"
          data-testid="confirm-supply-discard"
          disabled={selectedArmy === undefined}
          onClick={onConfirmDiscard}
        >
          Remove for Upkeep
        </button>
      </div>
    </section>
  );
}
